import React from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CapacityTile } from "@/components/common/CapacityTile";
import { useStorageRegions } from "@/hooks/useAdminStorage";
import { formatBytes } from "@/utils/formatBytes";

const toPercent = (ratio: number | null): number | null =>
  ratio === null ? null : ratio * 100;

export const StorageCapacityCard: React.FC = () => {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useStorageRegions();

  const reachableRegions = data?.regions.filter((region) => region.reachable);
  const unreachableChips = [
    ...(data?.errors ?? []).map((error) => ({
      region: error.region,
      message: error.message,
    })),
    ...(data?.regions ?? [])
      .filter(
        (region) =>
          !region.reachable &&
          !data?.errors.some((error) => error.region === region.region),
      )
      .map((region) => ({
        region: region.region,
        message: region.error ?? undefined,
      })),
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base font-semibold">
          {t("analytics.platform.storage.title")}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {isLoading && (
          <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
        )}
        {isError && (
          <p className="text-sm text-destructive">
            {t("analytics.platform.storage.error")}
          </p>
        )}
        {!isLoading && data && (
          <>
            {unreachableChips.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {unreachableChips.map((chip) => (
                  <Badge
                    key={chip.region}
                    variant="new_destructive"
                    title={chip.message}
                  >
                    {t("analytics.platform.storage.unreachable", {
                      region: chip.region,
                    })}
                  </Badge>
                ))}
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <CapacityTile
                label={t("analytics.platform.storage.aggregate")}
                value={`${formatBytes(data.usedBytes)} / ${formatBytes(data.totalBytes)}`}
                hint={t("analytics.platform.storage.percentUsed", {
                  percent: (data.usagePercent * 100).toFixed(1),
                })}
                bar={{ percent: data.usagePercent * 100 }}
              />
              {reachableRegions?.map((region) => (
                <CapacityTile
                  key={region.region}
                  label={region.region}
                  value={`${formatBytes(region.usedBytes)} / ${formatBytes(region.totalBytes)}`}
                  hint={
                    region.usagePercent === null
                      ? undefined
                      : t("analytics.platform.storage.percentUsed", {
                          percent: (region.usagePercent * 100).toFixed(1),
                        })
                  }
                  bar={{ percent: toPercent(region.usagePercent) }}
                />
              ))}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
